import React, { useCallback, useEffect, useState } from "react";
import BrandCard from "@components/card/BrandCard";
import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { BrandFilter } from "@pages/search/_components/index";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useApiRequest } from "@utils/ApiRequest";
import useAxios from "@libs/axios/useAxios";
import { BRAND_URL } from "@libs/apiUrl";
import { LOAD_SIZE_10 } from "@libs/reactQuery/queryClient";
import useIntersection from "@hook/useIntersection";
import Image from "@components/item/Image";
import SearchInput from "@components/input/SearchInput";
import { useNavigate, useSearchParams } from "react-router-dom";
import BasicDrawer from "@components/drawer/BasicDrawer";
import { useTranslation } from "react-i18next";

const Container = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "2.8rem",
  padding: "0 2rem",
});

const TitleBox = styled.div({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
});

const Title = styled.h3(({ theme }) => ({
  color: theme.color.black,
  fontSize: "2rem",
  fontWeight: 600,
  margin: 0,
  lineHeight: "2rem",
  display: "flex",
  justifyContent: "space-between",
}));

const BackButton = styled.button({
  display: "flex",
});

const InputWrapper = styled.div({
  width: "100%",
  display: "flex",
  alignItems: "center",
  gap: "2rem",
  padding: "2rem 0 ",
});

const BrandList = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "2rem",
});

const FilterLabelButton = styled.button(({ theme }) => ({
  display: "flex",
  textWrap: "nowrap",
  gap: "0.4rem",
  fontSize: "1.4rem",
  fontWeight: theme.fontWeight.medium,
  lineHeight: "1.6rem",
  padding: "0.8rem 0 0.8rem 0",
}));

const FilterItem = styled.button<{ isLast: boolean; focus: boolean }>(
  ({ theme, isLast, focus }) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "0.4rem",
    fontSize: "1.6rem",
    fontWeight: theme.fontWeight.medium,
    color: focus ? theme.color.green : theme.color.black,
    lineHeight: "1.6rem",
    cursor: "pointer",
    padding: "2rem 0",
    borderBottom: isLast ? "none" : `1px solid ${theme.color.border_gray}`,
  }),
);

const SearchBrand = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState("");
  const [searchParams] = useSearchParams();
  const [openDrawer, setOpenDrawer] = useState(false);
  const { t } = useTranslation();
  const searchParam = searchParams.get("query");
  const lang = window.localStorage.getItem("language");

  useEffect(() => {
    if (searchParam) {
      setInputValue(searchParam);
    }
  }, []);

  const { getBrandSortList } = useApiRequest();
  const { data: brandSortList, isLoading: brandSortLoading } = useQuery({
    queryKey: ["brandSortList"],
    queryFn: getBrandSortList,
  });

  // 기본값 설정
  const [selectedFilter, setSelectedFilter] = useState("ABC 순");
  const [selectedFilterEn, setSelectedFilterEn] = useState(
    "Alphabetical Order (A-Z)",
  );
  const [selectedFilterCode, setSelectedFilterCode] = useState("ALPHABET_ASC");
  const [selectedFilterDetail, setSelectedFilterDetail] = useState("ALL");

  const { axiosInstance } = useAxios();

  const getBrandList = useCallback(
    async ({ pageParam = 1 }) => {
      let url =
        selectedFilterDetail === "ALL"
          ? `${BRAND_URL}?page=${pageParam}&limit=${LOAD_SIZE_10}&order_by=${selectedFilterCode}&search_value=${inputValue}`
          : `${BRAND_URL}?page=${pageParam}&limit=${LOAD_SIZE_10}&order_by=${selectedFilterCode}&start_with=${selectedFilterDetail}&search_value=${inputValue}`;

      const res = await axiosInstance.get(url);
      if (res.status === 200) {
        return res.data;
      }
    },
    [axiosInstance, selectedFilterCode, inputValue, selectedFilterDetail],
  );

  const {
    data: brandList,
    fetchNextPage,
    isLoading,
  } = useInfiniteQuery({
    queryKey: [
      "brandList",
      selectedFilterCode,
      selectedFilterDetail,
      inputValue,
    ],
    queryFn: ({ pageParam = 1 }) => getBrandList({ pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPages, pages) =>
      pages?.length < lastPages.data.total_count / LOAD_SIZE_10
        ? pages.length + 1
        : undefined,
    enabled: !!inputValue,
  });

  const { setObserverTarget } = useIntersection(() => {
    fetchNextPage();
  });

  const handleBack = () => {
    navigate(-1);
  };

  const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const encodedQuery = encodeURIComponent(inputValue);
    if (e.key === "Enter") {
      navigate(`/search/brand?query=${encodedQuery}`);
    }
  };

  const handleOpenDrawer = () => {
    setOpenDrawer(true);
  };

  const handleFilterSelect = (filter: any) => {
    setSelectedFilter(filter.name_ko);
    setSelectedFilterEn(filter.name_en);
    setSelectedFilterCode(filter.code);
    setSelectedFilterDetail("ALL");
    setOpenDrawer(false);
  };

  return brandSortLoading ? null : (
    <Container>
      <InputWrapper>
        <BackButton onClick={handleBack}>
          <Image src={theme.image.arrow_left.default} />
        </BackButton>
        <SearchInput
          placeholder="아이템, 셀러 브랜드 명, 키워드 검색"
          inputValue={inputValue}
          setInputValue={setInputValue}
          onClick={handleKeyUp}
        />
      </InputWrapper>

      <TitleBox>
        <Title>{t("브랜드")}</Title>
        <FilterLabelButton onClick={handleOpenDrawer}>
          {lang === "en" ? selectedFilterEn : selectedFilter}
          <Image src={theme.image.arrow_down.default} size="1.6rem" />
        </FilterLabelButton>
      </TitleBox>

      <BrandList>
        {brandList?.pages?.map((page) =>
          page?.data?.list?.map((item: any) => (
            <BrandCard
              key={item.brand_id}
              id={item.brand_id}
              name={item.brand_name_en}
              koreanName={item.brand_name_ko}
              src={item.logo_url}
              liked={item.is_liked !== 0}
              withImage
              imageList={item.product_image_list}
            />
          )),
        )}
      </BrandList>

      <div ref={setObserverTarget} />

      <BasicDrawer open={openDrawer} setOpen={setOpenDrawer}>
        {brandSortList.map((filter: any, index: number) => (
          <FilterItem
            focus={filter.name_ko === selectedFilter}
            isLast={index === brandSortList.length - 1}
            key={filter.code}
            onClick={() => handleFilterSelect(filter)}>
            <span>{t(lang === "en" ? filter.name_en : filter.name_ko)}</span>
          </FilterItem>
        ))}
      </BasicDrawer>
    </Container>
  );
};

export default SearchBrand;
